import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { useInvestments } from "../api/investments";
import { Loading, ErrorState } from "../components/States";
import { formatCents } from "../lib/format";

const COLORS = ["#60a5fa", "#34d399", "#f59e0b", "#a78bfa", "#f472b6", "#22d3ee", "#facc15", "#94a3b8"];

export function Investments() {
  const inv = useInvestments();

  if (inv.isPending) return <Loading />;
  if (inv.isError) return <ErrorState onRetry={() => inv.refetch()} />;
  const data = inv.data;
  if (!data || data.total_value_cents === 0) {
    return (
      <div className="text-slate-400 text-sm py-8 text-center">
        No holdings yet. Once an investment account syncs, allocation and drift show up here.
      </div>
    );
  }

  const pie = data.allocation.map((a) => ({ name: a.asset_class, value: a.value_cents }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Investments</h1>
        <div className="text-sm text-slate-400">Total: {formatCents(data.total_value_cents)}</div>
      </div>
      <p className="text-xs text-slate-500">Observations about your portfolio — facts, not recommendations.</p>

      <section>
        <h2 className="text-sm font-medium text-slate-300 mb-2">Allocation by asset class</h2>
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}>
                  {pie.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 6, fontSize: 12 }}
                  formatter={(v: number) => formatCents(v)}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <dl className="space-y-1 text-sm self-center">
            {data.allocation.map((a, i) => (
              <div key={a.asset_class} className="flex items-center justify-between">
                <dt className="flex items-center gap-2">
                  <span className="inline-block h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                  {a.asset_class}
                </dt>
                <dd className="tabular-nums text-slate-400">
                  {a.pct}% · {formatCents(a.value_cents)}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section>
        <h2 className="text-sm font-medium text-slate-300 mb-2">Largest positions</h2>
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-4">
          {data.concentration.map((c) => (
            <div key={c.symbol} className="flex items-center justify-between border-b border-slate-800 py-2 last:border-b-0 text-sm">
              <span>
                {c.symbol}
                {c.flagged && (
                  <span className="ml-2 text-[10px] uppercase tracking-wide rounded bg-amber-900/60 text-amber-300 px-1.5 py-0.5">
                    &gt;20%
                  </span>
                )}
              </span>
              <span className="tabular-nums text-slate-400">{c.pct}% · {formatCents(c.value_cents)}</span>
            </div>
          ))}
        </div>
      </section>

      {data.drift && data.drift.entries.length > 0 && (
        <section>
          <h2 className="text-sm font-medium text-slate-300 mb-2">
            Drift since {data.drift.asof_date}
          </h2>
          <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-4">
            {data.drift.entries.map((d) => (
              <div key={d.asset_class} className="flex items-center justify-between border-b border-slate-800 py-2 last:border-b-0 text-sm">
                <span>{d.asset_class}</span>
                <span className={`tabular-nums ${d.delta_pct >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                  {d.delta_pct >= 0 ? "+" : ""}{d.delta_pct}pp ({d.prior_pct}% → {d.current_pct}%)
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {data.unclassified_symbols.length > 0 && (
        <p className="text-xs text-slate-500">
          Unclassified (awaiting classification): {data.unclassified_symbols.join(", ")}
        </p>
      )}
    </div>
  );
}
